/**
 * Timeline component for visualizing body state history.
 *
 * Shows color-coded blocks for each day's body state with responsive design.
 */

import React from 'react';
import { TimelineData } from '../../types';

interface TimelineProps {
  data: TimelineData[];
}

const BODY_STATE_COLORS: Record<string, string> = {
  recovery: 'bg-orange-400',
  balanced: 'bg-green-400',
  high_energy: 'bg-blue-400',
  hormonal_dip: 'bg-red-400',
  hormonal_shift: 'bg-purple-400'
};

const BODY_STATE_LABELS: Record<string, string> = {
  recovery: 'Recovery',
  balanced: 'Balanced',
  high_energy: 'High Energy',
  hormonal_dip: 'Hormonal Dip',
  hormonal_shift: 'Hormonal Shift'
};

const Timeline: React.FC<TimelineProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <p className="text-gray-600">No check-in data available for timeline view</p>
      </div>
    );
  }

  // Group by week for better mobile display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Body State Timeline</h3>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-6">
        {Object.entries(BODY_STATE_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded ${BODY_STATE_COLORS[key]}`}></div>
            <span className="text-xs text-gray-600">{label}</span>
          </div>
        ))}
      </div>

      {/* Timeline Grid */}
      <div className="grid grid-cols-7 md:grid-cols-14 gap-2">
        {data.map((item, index) => {
          const color = BODY_STATE_COLORS[item.state_type] || 'bg-gray-300';
          const label = BODY_STATE_LABELS[item.state_type] || item.state_type;

          return (
            <div
              key={index}
              className="group relative"
              title={`${formatDate(item.date)}: ${label}`}
            >
              <div
                className={`${color} rounded h-12 md:h-16 transition-transform hover:scale-110 cursor-pointer`}
              >
                <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                  <div>{formatDate(item.date)}</div>
                  <div className="font-semibold">{label}</div>
                  <div>Sleep: {item.sleep_quality}/5</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Hover over each block to see details
      </p>
    </div>
  );
};

export default Timeline;
